import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  X,
  Download,
  Send,
  ArrowLeftRight,
  QrCode,
  Filter,
  CheckCircle2,
  Users,
  DollarSign,
  ChevronRight,
  Plus,
  Minus,
  AlertCircle,
  Search,
  ShieldAlert,
  ArrowRight,
  Mail,
  Loader2
} from "lucide-react";
import { authApi } from "../../api/auth.api";
import { useSession } from "../../providers/SessionProvider";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { servicesList } from "../../data/servicesData";
import useEmblaCarousel from "embla-carousel-react";
import fondImageDesktop from "../../components/profileUser/assets/Fond-Large.jpg";
import fondImageMobile from "../../components/profileUser/assets/Fond.jpg";
import cardImage from "../../components/profileUser/card.jpg";
import slide1 from "../../components/profileUser/slide/slide1.png";
import slide2 from "../../components/profileUser/slide/slide2.png";
import slide3 from "../../components/profileUser/slide/slide3.png";
import slide4 from "../../components/profileUser/slide/slide4.png";
import { comptesApi, clientsApi } from "../../api/clients.api";
import { API_BASE_URL } from "../../api/client";
import { toast } from "../../hooks/use-toast";
import { KYCVerificationModal } from "../../components/profileUser/KYCVerificationModal";

// Helper function for safe number formatting
const formatMoney = (value, decimals = 2) => {
  const num = Number(value) || 0;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).replace(/,/g, " ");
};

// Composant Carousel pour les soldes
const SoldeCarousel = ({ selectedAccount }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const soldesActifs = [];
  // Robust extraction of balances (API might return camelCase or snake_case)
  const valSoldeS = selectedAccount.soldeS ?? selectedAccount.solde_S ?? 0;
  const valSoldeBS = selectedAccount.soldeBS ?? selectedAccount.solde_BS ?? 0;
  const valSoldePL = selectedAccount.soldePL ?? selectedAccount.solde_PL ?? 0;
  const valSoldeM = selectedAccount.solde_M ?? 0;


  // SOL account balance
  if (valSoldeS > 0)
    soldesActifs.push({
      label: "SOL",
      value: valSoldeS,
      color: "rgba(139, 92, 246, 0.3)",
    });
  if (valSoldeBS > 0)
    soldesActifs.push({
      label: "BS",
      value: valSoldeBS,
      color: "rgba(255, 255, 255, 0.25)",
    });
  if (valSoldePL > 0)
    soldesActifs.push({
      label: "Bousanm Solidè",
      value: valSoldePL,
      color: "rgba(59, 130, 246, 0.3)",
    });
  if (valSoldeM > 0)
    soldesActifs.push({
      label: "Mutuelle",
      value: valSoldeM,
      color: "rgba(34, 197, 94, 0.3)",
    });

  if (soldesActifs.length === 0) {
    return <div className="text-lg font-bold text-white">0.00</div>;
  }

  if (soldesActifs.length === 1) {
    const solde = soldesActifs[0];
    return (
      <div className="flex flex-col">
        <div
          className="px-2 py-0.5 rounded-lg self-start mb-1.5"
          style={{ backgroundColor: solde.color }}
        >
          <span className="text-[10px] font-bold text-white uppercase tracking-wider">
            {solde.label}
          </span>
        </div>
        <div className="text-lg font-bold text-white">
          {formatMoney(solde.value)}
        </div>
      </div>
    );
  }

  const currentSolde = soldesActifs[currentIndex];

  const changeSolde = () => {
    setCurrentIndex((prev) => (prev + 1) % soldesActifs.length);
  };

  return (
    <button onClick={changeSolde} className="flex flex-col text-left">
      <div className="flex items-center gap-1.5 mb-1.5">
        <div
          className="px-2 py-0.5 rounded-lg"
          style={{ backgroundColor: currentSolde.color }}
        >
          <span className="text-[10px] font-bold text-white uppercase tracking-wider">
            {currentSolde.label}
          </span>
        </div>

        <div className="flex gap-1">
          {soldesActifs.map((_, index) => (
            <div
              key={index}
              className={`h-1 rounded-full ${index === currentIndex ? "w-3 bg-white" : "w-1 bg-white/30"
                }`}
            />
          ))}
        </div>

        <ChevronRight className="w-3 h-3 text-white/60" />
      </div>

      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
        className="text-lg font-bold text-white"
      >
        {formatMoney(currentSolde.value)}
      </motion.div>
    </button>
  );
};

// Données mockées
const mockAccount = {
  code: "12345678",
  nom: "Dupont",
  prenom: "Jean",
  type_compte: "BS",
  devise: "HTG",
  solde_BS: 15000.5,
  solde_PL: 8500.25,
  solde_M: 2300.75,
  kycvalidation: "verified",
  nb_main: 5,
  touche: 25000.0,
  difference_touche: 1500.5,
  statut_touche: "en_avance",
  nb_jours_payer: 45,
  total_jours: 60,
  nb_jours_non_payer: 15,
  sol: 500,
  montant_non_payer: 7500,
  derniere_transaction: {
    montant_total: 1500.0,
    date: "29-10-2025 | 14:30:13",
  },
  last_6_transactions: [
    {
      id: "1",
      type: "depo_sol",
      date: "29-10-2025 | 14:30:13",
      montant_total: 1500.0,
      description: "Dépôt en espèces",
      created_by: "Admin",
      devise: "HTG",
    },
    {
      id: "2",
      type: "retrait",
      date: "28-10-2025 | 10:15:42",
      montant_total: 500.0,
      description: "Retrait guichet",
      created_by: "Agent 01",
      devise: "HTG",
    },
    {
      id: "3",
      type: "transfert",
      date: "27-10-2025 | 16:20:30",
      montant_total: 2000.0,
      description: "Transfert vers #87654321",
      created_by: "Agent 02",
      devise: "HTG",
    },
    {
      id: "4",
      type: "depo_sol",
      date: "26-10-2025 | 09:45:12",
      montant_total: 3000.0,
      description: "Dépôt chèque",
      created_by: "Admin",
      devise: "HTG",
    },
    {
      id: "5",
      type: "retrait",
      date: "25-10-2025 | 13:30:00",
      montant_total: 800.0,
      description: "Retrait ATM",
      created_by: "Système",
      devise: "HTG",
    },
    {
      id: "6",
      type: "depo_sol",
      date: "24-10-2025 | 11:00:25",
      montant_total: 1200.0,
      description: "Dépôt mobile",
      created_by: "Agent 03",
      devise: "HTG",
    },
  ],
  historique_transactions: [
    {
      id: "1",
      type: "depo_sol",
      date: "29-10-2025 | 14:30:13",
      montant: 1500.0,
      description: "Dépôt en espèces",
      devise: "HTG",
    },
    {
      id: "2",
      type: "retrait",
      date: "28-10-2025 | 10:15:42",
      montant: 500.0,
      description: "Retrait guichet",
      devise: "HTG",
    },
    {
      id: "3",
      type: "transfert",
      date: "27-10-2025 | 16:20:30",
      montant: 2000.0,
      description: "Transfert vers #87654321",
      devise: "HTG",
    },
    {
      id: "4",
      type: "depo_sol",
      date: "26-10-2025 | 09:45:12",
      montant: 3000.0,
      description: "Dépôt chèque",
      devise: "HTG",
    },
    {
      id: "5",
      type: "retrait",
      date: "25-10-2025 | 13:30:00",
      montant: 800.0,
      description: "Retrait ATM",
      devise: "HTG",
    },
    {
      id: "6",
      type: "depo_sol",
      date: "24-10-2025 | 11:00:25",
      montant: 1200.0,
      description: "Dépôt mobile",
      devise: "HTG",
    },
    {
      id: "7",
      type: "transfert",
      date: "23-10-2025 | 15:45:30",
      montant: 1800.0,
      description: "Transfert interne",
      devise: "HTG",
    },
    {
      id: "8",
      type: "depo_sol",
      date: "22-10-2025 | 08:30:15",
      montant: 2500.0,
      description: "Dépôt bancaire",
      devise: "HTG",
    },
  ],
};

const mockAccounts = [
  { ...mockAccount },
  {
    code: "87654321",
    nom: "Martin",
    prenom: "Sophie",
    type_compte: "SOL",
    devise: "USD",
    solde_S: 12000,
  },
  {
    code: "11223344",
    nom: "Dubois",
    prenom: "Pierre",
    type_compte: "BS",
    devise: "HTG",
    solde_BS: 20000,
  },
];

const getServices = (t) => [
  {
    id: "1",
    icon: Download,
    title: t('dashboard:services.depot'),
    route: "/construction",
    color: "bg-blue-600",
  },
  {
    id: "2",
    icon: Send,
    title: t('dashboard:services.retrait'),
    route: "/construction",
    color: "bg-blue-600",
  },
  {
    id: "3",
    icon: ArrowLeftRight,
    title: t('dashboard:services.transfert'),
    route: "/construction",
    color: "bg-blue-600",
  },
  {
    id: "4",
    icon: QrCode,
    title: t('dashboard:services.qrCode'),
    route: "/qrcode",
    color: "bg-blue-600",
  },
];



// Modal de restriction
const RestrictionModal = ({ isOpen, onClose, currentLanguage }) => {
  const { t } = useTranslation(['dashboard', 'common', 'services']);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-white/90 p-6 shadow-2xl backdrop-blur-xl border border-white/50"
          >
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100/80 shadow-inner">
                <ShieldAlert className="h-8 w-8 text-orange-600" />
              </div>

              <h3 className="mb-2 text-xl font-bold text-gray-900">
                {t('dashboard:verification.restrictionTitle')}
              </h3>

              <p className="mb-6 text-sm text-gray-600 leading-relaxed">
                {t('dashboard:verification.restrictionMessage')}
              </p>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-red-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-500/30"
              >
                {t('dashboard:verification.understood')}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { userData, selectedAccount, setSelectedAccount, refreshUserData } = useSession(); // Access global cache + shared account state
  const { user } = useAuth();

  // Normalized accounts state (derived from userData)

  // Normalized accounts state (derived from userData)

  const [planSolideStats, setPlanSolideStats] = useState(null);

  // UI State
  const [isVisible, setIsVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [restrictionModalOpen, setRestrictionModalOpen] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterDateRange, setFilterDateRange] = useState("all");
  const [specificDate, setSpecificDate] = useState(new Date());
  const { t, i18n } = useTranslation(['dashboard', 'common', 'services', 'transactions', 'settings']);
  const currentLanguage = i18n.language;
  const [scrolled, setScrolled] = useState(false);
  const [languageModalVisible, setLanguageModalVisible] = useState(false);

  // KYC State & Logic
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [kycVerificationUrl, setKycVerificationUrl] = useState("");
  const [isKycLoading, setIsKycLoading] = useState(false);
  const kycPollIntervalRef = React.useRef(null);

  useEffect(() => {
    return () => {
      if (kycPollIntervalRef.current) {
        clearInterval(kycPollIntervalRef.current);
      }
    };
  }, []);

  const startKycPolling = (username) => {
    if (kycPollIntervalRef.current) {
      clearInterval(kycPollIntervalRef.current);
    }

    kycPollIntervalRef.current = setInterval(async () => {
      try {
        const response = await clientsApi.getKycStatus(username);

        if (response.status === 'verified' || response.status === 'review') {
          clearInterval(kycPollIntervalRef.current);
          setKycModalOpen(false);
          toast({
            title: t('common:success'),
            description: t('settings:kyc.success'),
            type: "success",
          });
          if (refreshUserData) refreshUserData();
        } else if (response.status === 'rejected' || response.status === 'failed') {
          clearInterval(kycPollIntervalRef.current);
          setKycModalOpen(false);
          toast({
            title: t('common:error'),
            description: t('settings:kyc.failed'),
            type: "error",
          });
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 5000);
  };

  const handleStartKycVerification = async () => {
    const username = user?.email || user?.telephone;
    if (!username) {
      toast({
        title: t('common:error'),
        description: t('settings:noEmailOrPhone'),
        type: "error",
      });
      return;
    }

    setIsKycLoading(true);
    try {
      const response = await clientsApi.startKycVerification(username);
      if (response.success && response.verification_url) {
        setKycVerificationUrl(response.verification_url);
        setKycModalOpen(true);
        startKycPolling(username);
      } else {
        toast({
          title: t('common:error'),
          description: t('settings:kyc.startError'),
          type: "error",
        });
      }
    } catch (error) {
      console.error("KYC Error:", error);
      toast({
        title: t('common:error'),
        description: error?.response?.data?.message || t('settings:kyc.error'),
        type: "error",
      });
    } finally {
      setIsKycLoading(false);
    }
  };

  const services = getServices(t);

  // Handle service click with restriction check
  const handleServiceClick = (service, isQuickAction = false) => {
    // If pending verification, check restrictions
    const userStatus = user?.statut || selectedAccount?.statut || 'active';
    if (userStatus === 'pending_verification') {
      let isRestricted = false;

      if (isQuickAction) {
        // Quick Actions: Dépôt, Retrait, Transfert, QR Code are restricted
        // Title based check as IDs are strings "1","2","3","4"
        const restrictedTitles = ["Dépôt", "Retrait", "Transfert", "QR Code"];
        if (restrictedTitles.includes(service.title)) {
          isRestricted = true;
        }
      } else {
        // Main Services Grid
        // Restricted IDs: 1 (Bousanm Solidè), 7 (Acheter crédit)
        const restrictedIds = [1, 7];
        if (restrictedIds.includes(service.id)) {
          isRestricted = true;
        }
      }

      if (isRestricted) {
        setRestrictionModalOpen(true);
        return;
      }
    }

    // Default action
    navigate(service.route);
  };

  const partnerSlides = [
    slide1,
    slide2,
    slide3,
    slide4,
  ];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [activeIndex, setActiveIndex] = useState(0);

  // Security Reminder State
  const [showSecurityReminder, setShowSecurityReminder] = useState(false);

  useEffect(() => {
    // Only check reminder if data is loaded
    if (userData.isLoading || !userData.securityStatus) return;

    const checkSecurity = () => {
      // Check if reminder was dismissed recently
      const lastDismissed = localStorage.getItem('security_reminder_last_dismissed');
      if (lastDismissed) {
        const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
        const timeSinceDismissal = Date.now() - parseInt(lastDismissed, 10);
        if (timeSinceDismissal < threeDaysInMs) return;
      }

      if (!userData.securityStatus.hasSecurityQuestions) {
        setShowSecurityReminder(true);
      }
    };
    checkSecurity();
  }, [userData.securityStatus, userData.isLoading]);

  const handleDismissReminder = () => {
    // Save current timestamp to localStorage
    localStorage.setItem('security_reminder_last_dismissed', Date.now().toString());
    setShowSecurityReminder(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Normalized accounts state (derived from userData with useMemo for instant updates)
  const normalizedAccounts = useMemo(() => {
    if (!userData.accounts) return [];

    const accountsList = userData.accounts;

    // Normalize field names (backend returns snake_case sometimes)
    return accountsList.map(acc => {
      let imageUrl = acc.client?.imageUrl || acc.image || null;
      if (imageUrl && imageUrl.startsWith('/uploads')) {
        imageUrl = `${API_BASE_URL}${imageUrl}`;
      }

      return {
        ...acc,
        solde_BS: acc.soldeBS ?? acc.solde_BS ?? 0,
        solde_S: acc.soldeS ?? acc.solde_S ?? 0,
        solde_PL: acc.soldePL ?? acc.solde_PL ?? 0,
        solde_M: acc.solde_M ?? 0,
        type_compte: acc.type || acc.type_compte || 'BS',
        nom: acc.client?.nom || acc.nom || '',
        prenom: acc.client?.prenom || acc.prenom || '',
        image: imageUrl,
        kycvalidation: acc.client?.kycStatus || acc.kycvalidation || 'unverified',
        statut: acc.client?.statut || acc.statut || 'active',
        last_6_transactions: acc.last_6_transactions || [],
        historique_transactions: acc.last_6_transactions || [],
      };
    });
  }, [userData.accounts]);

  // Handle Default Account Selection
  useEffect(() => {
    if (!selectedAccount && normalizedAccounts.length > 0) {
      setSelectedAccount(normalizedAccounts[0]);
    }
  }, [normalizedAccounts, selectedAccount, setSelectedAccount]);

  // Fetch Plan Solide stats for BS accounts
  useEffect(() => {
    const fetchStats = async () => {
      const accountType = selectedAccount?.type_compte || selectedAccount?.type;
      if (selectedAccount?.code && accountType === 'BS') {
        try {
          const stats = await comptesApi.getPlanSolideStats(selectedAccount.code);
          if (stats && !stats.error) {
            setPlanSolideStats(stats);
          } else {
            setPlanSolideStats(null);
          }
        } catch (e) {
          console.error("Failed to fetch plan solide stats", e);
          setPlanSolideStats(null);
        }
      } else {
        setPlanSolideStats(null);
      }
    };
    fetchStats();
  }, [selectedAccount]);

  // Banner Logic
  const handleBannerClick = () => {
    const fullName = userData?.accounts?.[0]?.client?.nom ? `${userData.accounts[0].client.prenom} ${userData.accounts[0].client.nom}` : '';
    const accountNum = selectedAccount?.code || '';

    const message = t('dashboard:verification.contactMessage', { name: fullName, accountNumber: accountNum });

    const url = `https://wa.me/50940128282?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const loading = userData.isLoading;
  const error = userData.error;
  const accounts = normalizedAccounts;

  useEffect(() => {
    if (!emblaApi) return;
    const update = () => setActiveIndex(emblaApi.selectedScrollSnap());
    update();
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    const id = setInterval(() => {
      try {
        emblaApi.scrollNext();
      } catch { }
    }, 3000);
    return () => {
      emblaApi.off("select", update);
      emblaApi.off("reInit", update);
      clearInterval(id);
    };
  }, [emblaApi]);

  const languages = [
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ht", name: "Kreyòl", flag: "🇭🇹" },
    { code: "es", name: "Español", flag: "🇪🇸" },
  ];

  const getAvatarColor = (nom, prenom) => {
    const colors = [
      "#0047D1",
      "#1d4ed8",
      "#3b82f6",
      "#06b6d4",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
      "#14b8a6",
    ];
    const initials = `${nom || ""}${prenom || ""}`;
    const charCode = initials.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  const getFilteredTransactions = () => {
    let filtered = [...selectedAccount.historique_transactions];

    if (filterType !== "all") {
      filtered = filtered.filter((t) => t.type === filterType);
    }

    if (filterDateRange !== "all") {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      filtered = filtered.filter((t) => {
        const [datePart] = t.date.split(" | ");
        const [day, month, year] = datePart.split("-");
        const transDate = new Date(year, month - 1, day);
        transDate.setHours(0, 0, 0, 0);

        switch (filterDateRange) {
          case "today":
            return transDate.getTime() === now.getTime();
          case "week":
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return transDate.getTime() >= weekAgo.getTime();
          case "month":
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return transDate.getTime() >= monthAgo.getTime();
          case "custom":
            const specific = new Date(specificDate);
            specific.setHours(0, 0, 0, 0);
            return transDate.getTime() === specific.getTime();
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const getUniqueTransactionTypes = () => {
    const types = selectedAccount.historique_transactions.map((t) => t.type);
    return [...new Set(types)];
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de vos comptes...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <div className="text-center p-6 bg-white rounded-xl shadow-lg max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Show empty state only if no accounts found after loading
  if (accounts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <div className="text-center p-6 bg-white rounded-xl shadow-lg max-w-md">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Aucun compte trouvé</h2>
          <p className="text-gray-600">Vous n'avez pas encore de compte actif.</p>
        </div>
      </div>
    );
  }

  // Safety guard: If accounts exist but selectedAccount is not yet set (waiting for effect)
  if (!selectedAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* Image de fond fixe - Mobile */}
      <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 md:hidden"
        style={{
          backgroundImage: `url(${fondImageMobile})`,
        }}
      />
      {/* Image de fond fixe - Desktop / Tablet */}
      <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 hidden md:block"
        style={{
          backgroundImage: `url(${fondImageDesktop})`,
        }}
      />
      {/* Header */}
      <div
        className={`${scrolled ? "bg-white/40 backdrop-blur-md " : ""
          } sticky top-0 z-40 md:hidden`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* User Info */}
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold border-2 overflow-hidden"
                style={{
                  backgroundColor: selectedAccount?.image
                    ? "transparent"
                    : getAvatarColor(selectedAccount?.nom, selectedAccount?.prenom),
                  borderColor: getAvatarColor(selectedAccount?.nom, selectedAccount?.prenom),
                }}
              >
                {selectedAccount?.image ? (
                  <img
                    src={selectedAccount.image}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>
                    {selectedAccount?.nom?.charAt(0)}
                    {selectedAccount?.prenom?.charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t('dashboard:hello')}, {selectedAccount.nom} {selectedAccount.prenom}
                </p>

                <div className="flex">
                  <button
                    onClick={() => setModalVisible(true)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    #{selectedAccount.code}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {(user?.statut === 'pending_verification' || user?.role === 'TempUser' || selectedAccount?.statut === 'pending_verification' || selectedAccount?.kycvalidation !== 'verified') && (
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleStartKycVerification}
                      disabled={isKycLoading}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-600 rounded-full border border-red-100 shadow-sm active:bg-red-100 transition-colors"
                    >
                      {isKycLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertCircle className="w-3.5 h-3.5" />}
                      <span className="text-[10px] font-bold uppercase tracking-wide">
                        {t('common:navbar.unverified')}
                      </span>
                    </motion.button>
                  )}
                </div>
              </div>
            </div>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLanguageModalVisible(!languageModalVisible)}
                className="text-2xl hover:scale-110 transition-transform"
              >
                {languages.find((l) => l.code === currentLanguage)?.flag}
              </button>

              <AnimatePresence>
                {languageModalVisible && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={async () => {
                          await i18n.changeLanguage(lang.code);
                          setLanguageModalVisible(false);
                          toast({
                            title: t('common:success'),
                            description: t('common:languageChanged'),
                            type: "success",
                          });
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${i18n.language === lang.code ? "bg-blue-50" : ""
                          }`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span
                          className={`text-sm font-medium ${currentLanguage === lang.code
                            ? "text-blue-600"
                            : "text-gray-700"
                            }`}
                        >
                          {lang.name}
                        </span>
                        {currentLanguage === lang.code && (
                          <CheckCircle2 className="w-4 h-4 text-blue-600 ml-auto" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-6 md:px-16 lg:px-32 md:mt-6">
        <div className="grid gap-4 md:gap-6 md:grid-cols-12">
          {/* Balance Card */}
          {(user?.statut === 'pending_verification' || selectedAccount?.statut === 'pending_verification') && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 md:mb-0 md:col-span-12"
            >
              <button
                onClick={handleBannerClick}
                className="w-full relative overflow-hidden rounded-2xl border border-orange-200 bg-orange-50/90 shadow-lg shadow-orange-100/50 backdrop-blur-md transition-transform active:scale-[0.99] hover:scale-[1.01]"
              >
                <div className="flex items-center p-4">
                  {/* Icon Circle */}
                  <div className="mr-3.5 flex h-12 w-12 items-center justify-center rounded-full bg-white/80 shadow-sm shadow-orange-200/50">
                    <Mail className="h-6 w-6 text-orange-500" />
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 text-left">
                    <h4 className="mb-0.5 font-bold text-orange-900">
                      {t('dashboard:verification.pendingTitle')}
                    </h4>
                    <p className="text-xs font-medium text-orange-700">
                      {t('dashboard:verification.pendingMessage')}
                    </p>
                  </div>

                  {/* Chevron */}
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/50">
                    <ChevronRight className="h-4 w-4 text-orange-600" />
                  </div>
                </div>
              </button>
            </motion.div>
          )}

          {/* Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-2xl mb-4 md:mb-0 md:col-span-7 lg:col-span-8"
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 backdrop-blur-sm"
              style={{
                backgroundImage: `url(${cardImage})`,
                filter: "blur(4px)",
                transform: "scale(1.1)",
              }}
            />

            <div className="relative p-4">
              {/* Static badge - Account type/currency only */}
              <div className="absolute top-3 right-3 bg-black/40 px-3 py-1.5 rounded-full flex items-center gap-2">
                <span className="text-xs font-medium text-white">
                  {selectedAccount.type_compte}
                </span>
                <span className="text-xs text-gray-200">
                  {selectedAccount.devise}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <span className="text-xs font-medium text-white">
                  {t('dashboard:balance.available')}
                </span>
                <button onClick={() => setIsVisible(!isVisible)}>
                  {isVisible ? (
                    <Eye className="w-4 h-4 text-white" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-white" />
                  )}
                </button>
              </div>

              {isVisible ? (
                <SoldeCarousel selectedAccount={selectedAccount} />
              ) : (
                <div className="text-2xl font-bold text-white">********</div>
              )}


            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-4 gap-3 mb-4 md:mb-0 md:col-span-5 lg:col-span-4"
          >
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <motion.button
                  key={service.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleServiceClick(service, true)}
                  className="flex flex-col items-center"
                >
                  <div
                    className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-2 border border-gray-300`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">
                    {service.title}
                  </span>
                </motion.button>
              );
            })}
          </motion.div>

          {/* Statistics Card for SOL Account */}
          {/* Statistics Card for SOL Account */}
          {((selectedAccount?.type_compte === "SOL" || selectedAccount?.type_compte === "sol") ||
            (selectedAccount?.type === "SOL" || selectedAccount?.type === "sol")) &&
            !planSolideStats?.progression && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/50 overflow-hidden mb-4 md:col-span-12"
              >
                <div className="p-4">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h3 className="text-[15px] font-bold text-slate-900">
                        {t('dashboard:statistics.title')}
                      </h3>
                      <p className="text-[11px] font-bold text-slate-600">
                        {t('dashboard:statistics.paymentTracking')}
                      </p>
                    </div>
                    <div
                      className={`px-2.5 py-1 rounded-full border ${selectedAccount.statut_touche === "en_avance"
                        ? "bg-green-500/20 border-green-500/40 text-green-700"
                        : selectedAccount.statut_touche === "en_regle"
                          ? "bg-amber-500/20 border-amber-500/40 text-amber-700"
                          : "bg-red-500/20 border-red-500/40 text-red-700"
                        }`}
                    >
                      <span className="text-[9px] font-medium">
                        {selectedAccount.statut_touche === "en_avance"
                          ? t('dashboard:sol.status.creditor')
                          : selectedAccount.statut_touche === "en_regle"
                            ? t('dashboard:sol.status.balanced')
                            : t('dashboard:sol.status.debtor')}
                      </span>
                    </div>
                  </div>

                  {/* Progress des jours */}
                  <div className="mb-3">
                    <div className="flex justify-between items-baseline mb-1.5">
                      <div>
                        <p className="text-[11px] font-bold text-slate-600">
                          {t('dashboard:sol.progressionPayments')}
                        </p>
                        <p className="text-[11px] font-bold text-slate-600 mt-px">
                          {selectedAccount.nb_jours_payer || 0}/
                          {selectedAccount.total_jours || 0} {t('dashboard:sol.days')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[15px] font-bold text-green-700">
                          {(() => {
                            const payer = selectedAccount.nb_jours_payer || 0;
                            const total = selectedAccount.total_jours || 0;
                            return total > 0 ? ((payer / total) * 100).toFixed(1) : 0;
                          })()}
                          %
                        </p>
                        <p className="text-[8px] font-bold text-green-700">
                          accompli
                        </p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-200/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${(() => {
                            const payer = selectedAccount.nb_jours_payer || 0;
                            const total = selectedAccount.total_jours || 0;
                            return total > 0 ? (payer / total) * 100 : 0;
                          })()}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="flex gap-2 mb-3">
                    {/* Jours payés */}
                    <div className="flex-1 bg-green-50/60 rounded-lg p-2 border border-green-500/30">
                      <p className="text-[9px] font-bold text-green-800 mb-0.5">
                        {t('dashboard:sol.daysPaid')}
                      </p>
                      <p className="text-[15px] font-bold text-slate-900 mb-0.5">
                        {selectedAccount.nb_jours_payer || 0}
                      </p>
                      <p className="text-[8px] font-normal text-green-700">
                        {t('dashboard:sol.days')}
                      </p>
                    </div>

                    {/* Jours non payés */}
                    <div className="flex-1 bg-amber-50/60 rounded-lg p-2 border border-amber-500/30">
                      <p className="text-[9px] font-bold text-amber-800 mb-0.5">
                        {t('dashboard:sol.daysRemaining')}
                      </p>
                      <p className="text-[15px] font-bold text-amber-900 mb-0.5">
                        {selectedAccount.nb_jours_non_payer || 0}
                      </p>
                      <p className="text-[8px] font-normal text-amber-700">
                        {t('dashboard:sol.days')}
                      </p>
                    </div>
                  </div>

                  {/* Montant non payé */}
                  <div className="bg-red-50/60 rounded-xl p-2.5 mb-2 border border-red-500/30">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-[11px] font-bold text-red-900">
                          {t('dashboard:sol.amountRemaining')}
                        </p>
                        <p className="text-[9px] font-normal text-red-700">
                          {selectedAccount.nb_jours_non_payer || 0} {t('dashboard:sol.days')} ×{" "}
                          {selectedAccount.sol || 0} {selectedAccount.devise || "HTG"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[18px] font-bold text-red-800">
                          {formatMoney(selectedAccount.montant_non_payer || 0)}
                          <span className="text-[10px] ml-1">
                            {selectedAccount.devise || "HTG"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dernière transaction */}
                  {selectedAccount.derniere_transaction && (
                    <div className="pt-2 border-t border-slate-200/30">
                      <div className="flex justify-between items-center">
                        <p className="text-[10px] font-bold text-slate-500">
                          {t('dashboard:sol.lastTransaction')}
                        </p>
                        <p className="text-[11px] font-bold text-slate-900">
                          {formatMoney(
                            selectedAccount.derniere_transaction.montant || 0
                          )}{" "}
                          {selectedAccount.devise || "HTG"}
                        </p>
                      </div>
                      <p className="text-[9px] font-bold text-slate-400 mt-0.5">
                        {new Date(selectedAccount.derniere_transaction.date).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

          {/* Statistics Card for Plan Solide (BS) */}
          {planSolideStats && planSolideStats.progression && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/50 overflow-hidden mb-4 md:col-span-12"
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-[15px] font-bold text-slate-900">
                      {t('dashboard:sol.bousanmSolide')}
                    </h3>
                    <p className="text-[11px] font-bold text-slate-600">
                      {t('dashboard:sol.plan')} {planSolideStats.compte?.plan || "N/A"}
                    </p>
                  </div>
                  <div
                    className={`px-2.5 py-1 rounded-full border ${planSolideStats.situation_actuelle?.est_a_jour
                      ? "bg-green-500/20 border-green-500/40 text-green-700"
                      : "bg-red-500/20 border-red-500/40 text-red-700"
                      }`}
                  >
                    <span className="text-[9px] font-medium">
                      {planSolideStats.situation_actuelle?.est_a_jour
                        ? t('dashboard:planSolide.upToDate')
                        : t('dashboard:planSolide.late')}
                    </span>
                  </div>
                </div>

                {/* Progress Bar (Weeks) */}
                <div className="mb-3">
                  <div className="flex justify-between items-baseline mb-1.5">
                    <div>
                      <p className="text-[11px] font-bold text-slate-600">
                        {t('dashboard:sol.progressionPlan')}
                      </p>
                      <p className="text-[11px] font-bold text-slate-600 mt-px">
                        {planSolideStats.progression?.semaines_payees || 0}/
                        {planSolideStats.progression?.total_semaines || 0}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[15px] font-bold text-green-700">
                        {(
                          Number(planSolideStats.progression?.pourcentage_completion) || 0
                        ).toFixed(1)}
                        %
                      </p>
                      <p className="text-[8px] font-bold text-green-700">
                        accompli
                      </p>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-200/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 rounded-full transition-all duration-500"
                      style={{
                        width: `${Number(planSolideStats.progression?.pourcentage_completion) || 0
                          }%`,
                      }}
                    />
                  </div>
                </div>

                {/* Stats Grid Compact */}
                <div className="flex gap-2 mb-3">
                  {/* Montant Versé */}
                  <div className="flex-1 bg-green-50/60 rounded-lg p-2 border border-green-500/30">
                    <p className="text-[9px] font-bold text-slate-500 mb-0.5">
                      {t('dashboard:planSolide.paid')}
                    </p>
                    <p className="text-[15px] font-bold text-slate-900 mb-0.5">
                      {formatMoney(
                        planSolideStats.financier?.montant_verse || 0
                      )}
                    </p>
                    <p className="text-[8px] font-normal text-slate-400">
                      {selectedAccount?.devise || "HTG"}
                    </p>
                  </div>

                  {/* Montant Restant Total */}
                  <div className="flex-1 bg-amber-50/60 rounded-lg p-2 border border-amber-500/30">
                    <p className="text-[9px] font-bold text-amber-800 mb-0.5">
                      {t('dashboard:planSolide.remainingTotal')}
                    </p>
                    <p className="text-[15px] font-bold text-amber-900 mb-0.5">
                      {formatMoney(
                        planSolideStats.financier?.montant_restant || 0
                      )}
                    </p>
                    <div className="flex justify-between items-center">
                      <p className="text-[8px] font-normal text-amber-800">
                        {selectedAccount?.devise || "HTG"}
                      </p>
                      <p className="text-[8px] font-medium text-amber-700">
                        {(planSolideStats.progression?.total_semaines || 0) -
                          (planSolideStats.progression?.semaines_payees ||
                            0)}{" "}
                        {t('dashboard:planSolide.weeks')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bonis */}
                <div className="bg-purple-50/60 rounded-xl p-2.5 mb-2 border border-purple-500/30">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[11px] font-bold text-purple-900">
                        {t('dashboard:planSolide.bonusAccumulated')}
                      </p>
                      <p className="text-[9px] font-normal text-purple-700">
                        {t('dashboard:planSolide.punctuality')}:{" "}
                        {(
                          Number(planSolideStats.progression?.pourcentage_ponctualite) || 0
                        ).toFixed(0)}
                        %
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[18px] font-bold text-purple-800">
                        {formatMoney(
                          planSolideStats.financier?.bonis_actuel || 0
                        )}
                        <span className="text-[10px] ml-1">
                          {selectedAccount?.devise || "HTG"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Prochaine Échéance */}
                {planSolideStats.situation_actuelle?.prochaine_echeance && (
                  <div className="pt-2 border-t border-slate-200/30">
                    <div className="flex justify-between items-center">
                      <p className="text-[10px] font-bold text-slate-500">
                        {t('dashboard:planSolide.nextDue')}
                      </p>
                      <p className="text-[11px] font-bold text-slate-900">
                        {new Date(
                          planSolideStats.situation_actuelle.prochaine_echeance + 'T12:00:00'
                        ).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Services Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-4 md:mb-0 md:col-span-8 lg:col-span-8"
          >
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              {t('dashboard:services.title')}
            </h2>
            {filterDateRange === "custom" && (
              <div className="mb-4">
                <input
                  type="date"
                  value={specificDate.toISOString().split("T")[0]}
                  onChange={(e) => setSpecificDate(new Date(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            )}

            <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-4 border border-gray-200/50">
              <div className="grid grid-cols-4 gap-4">
                {(showAllServices
                  ? servicesList
                  : servicesList.slice(0, 7)
                ).map((service) => (
                  <motion.button
                    key={service.id}
                    onClick={() => handleServiceClick(service, false)}
                    className="flex flex-col items-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="w-14 h-14 bg-white/80 rounded-2xl flex items-center justify-center mb-2 shadow-sm border border-white/50 overflow-hidden relative">
                      {/* Background gradient hint */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-50" />

                      {service.logo ? (
                        <img
                          src={service.logo}
                          alt={service.name}
                          className="w-full h-full object-cover p-1"
                        />
                      ) : (
                        <div className="text-blue-600 relative z-10">
                          {service.icon}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] font-medium text-gray-700 text-center leading-tight line-clamp-2 px-1">
                      {t(service.nameKey)}
                    </span>
                  </motion.button>
                ))}

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAllServices(!showAllServices)}
                  className="flex flex-col items-center"
                >
                  <div className="w-14 h-14 bg-gray-100/80 rounded-2xl flex items-center justify-center mb-2 border border-dashed border-gray-300">
                    {showAllServices ? (
                      <Minus className="w-6 h-6 text-gray-500" />
                    ) : (
                      <Plus className="w-6 h-6 text-gray-500" />
                    )}
                  </div>
                  <span className="text-[10px] font-semibold text-gray-500 text-center">
                    {showAllServices ? t('dashboard:services.viewLess') : t('dashboard:services.viewMore')}
                  </span>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Partners Carousel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4 md:col-span-4 lg:col-span-4"
          >
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              {t('dashboard:partners.title')}
            </h2>
            <div ref={emblaRef} className="overflow-hidden">
              <div className="flex gap-4">
                {partnerSlides.map((src, idx) => (
                  <div
                    key={src}
                    className={`min-w-[98%] sm:min-w-[90%] md:min-w-[90%] lg:min-w-[85%] transition-opacity duration-300 ${idx === activeIndex ? "opacity-100" : "opacity-40"
                      }`}
                  >
                    <div className="rounded-xl overflow-hidden border border-blue-200/50 bg-white/50 relative md:h-48 lg:h-56">
                      <div className="w-full pt-[56.25%] md:hidden" />
                      <img
                        src={src}
                        alt={`Partenaire ${idx + 1}`}
                        className="absolute inset-0 w-full h-full object-cover md:static md:inset-auto md:w-full md:h-full md:object-cover"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Recent Transactions */}
          {selectedAccount.last_6_transactions &&
            selectedAccount.last_6_transactions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-4 md:col-span-8 lg:col-span-8"
              >
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  {t('dashboard:transactions.title')}
                </h2>
                {selectedAccount.last_6_transactions.map(
                  (transaction, index) => (
                    <motion.div
                      key={transaction.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className="bg-white/40 backdrop-blur-sm rounded-xl p-4 mb-3 border border-gray-200/60"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-3">
                              <div
                                className={`px-2 py-0.2 rounded-full ${transaction.type === "depo_sol"
                                  ? "bg-blue-100/70 text-blue-700"
                                  : "bg-yellow-100/70 text-yellow-700"
                                  }`}
                              >
                                <span className="text-[8px] font-bold text-center">
                                  {transaction.type === 'depo_sol' ? 'DÉPÔT SOL' :
                                   transaction.type === 'depo_bs' ? 'DÉPÔT BS' :
                                   transaction.type === 'retrait_bs' ? 'RETRAIT BS' :
                                   transaction.type.toUpperCase()}
                                </span>
                              </div>
                              <span className="text-[8px] text-gray-500">
                                {new Date(transaction.date).toLocaleString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600">
                            {transaction.description}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-gray-900">
                            {formatMoney(transaction.montant_total || transaction.montant)}
                          </p>
                          <p className="text-xs font-medium text-gray-500">
                            {transaction.devise}
                          </p>
                        </div>
                      </div>
                      <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                        <p className="text-xs text-gray-400">
                          ID: {transaction.id.substring(0, 8)}...
                        </p>
                        {transaction.created_by_username && (
                          <p className="text-[10px] font-medium text-blue-900/60">
                            {t('dashboard:transactions.by')}{transaction.created_by_username}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )
                )}

                {selectedAccount.historique_transactions &&
                  selectedAccount.historique_transactions.length > 0 && (
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/history")}
                      className="w-full bg-blue-900 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 font-bold text-sm mt-2"
                    >
                      {t('dashboard:transactions.seeMore')}
                      <ChevronDown className="w-5 h-5" />
                    </motion.button>
                  )}

                {/* Full history will render in a separate grid item when showAllTransactions is true */}
              </motion.div>
            )}

          {/* Full Transaction History - separate grid column */}
          {showAllTransactions && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-4 md:col-span-4 lg:col-span-4"
            >
              <div className="flex items-center my-4">
                <div className="flex-1 h-0.5 bg-blue-900/20" />
                <span className="px-4 text-xs font-bold text-blue-900 tracking-wider">
                  HISTORIQUE COMPLET
                </span>
                <div className="flex-1 h-0.5 bg-blue-900/20" />
              </div>

              <div className="bg-white/40 backdrop-blur-sm rounded-xl p-4 mb-4 border border-gray-200/60">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full flex justify-between items-center -mb-1"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-blue-900" />
                    <span className="text-sm font-bold text-blue-900">
                      Filtres
                    </span>
                    {(filterType !== "all" || filterDateRange !== "all") && (
                      <div className="bg-blue-900 text-white w-5 h-5 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold">
                          {(filterType !== "all" ? 1 : 0) +
                            (filterDateRange !== "all" ? 1 : 0)}
                        </span>
                      </div>
                    )}
                  </div>
                  {showFilters ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>

                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="my-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          {t('dashboard:filters.period')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: t('dashboard:filters.all'), value: "all" },
                            { label: t('dashboard:filters.today'), value: "today" },
                            { label: t('dashboard:filters.sevenDays'), value: "week" },
                            { label: t('dashboard:filters.thirtyDays'), value: "month" },
                          ].map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setFilterDateRange(option.value)}
                              className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${filterDateRange === option.value
                                ? "bg-blue-900 text-white border-blue-900"
                                : "bg-white/50 text-gray-600 border-gray-100 hover:border-blue-900"
                                }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          {t('dashboard:filters.transactionType')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setFilterType("all")}
                            className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${filterType === "all"
                              ? "bg-blue-900 text-white border-blue-900"
                              : "bg-white/50 text-gray-600 border-gray-100 hover:border-blue-900"
                              }`}
                          >
                            {t('dashboard:filters.all')}
                          </button>
                          {getUniqueTransactionTypes().map((type) => (
                            <button
                              key={type}
                              onClick={() => setFilterType(type)}
                              className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${filterType === type
                                ? "bg-blue-900 text-white border-blue-900"
                                : "bg-white/50 text-gray-600 border-gray-100 hover:border-blue-900"
                                }`}
                            >
                              {type.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>

                      {(filterType !== "all" || filterDateRange !== "all") && (
                        <button
                          onClick={() => {
                            setFilterType("all");
                            setFilterDateRange("all");
                          }}
                          className="mt-3 px-4 py-2 bg-red-50 text-red-600 rounded-full text-xs font-medium flex items-center gap-2 hover:bg-red-100 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          {t('common:reset')}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex justify-between items-center mb-3 px-1">
                <p className="text-xs font-medium text-gray-600">
                  {t('dashboard:filters.transactionsFound', { count: getFilteredTransactions().length })}
                </p>
                {getFilteredTransactions().length > 0 && (
                  <p className="text-xs text-gray-400">
                    {t('common:total')}:{" "}
                    {formatMoney(getFilteredTransactions().reduce((sum, t) => sum + (Number(t.montant) || 0), 0))}{" "}
                    HTG
                  </p>
                )}
              </div>

              {getFilteredTransactions().length > 0 ? (
                getFilteredTransactions().map((transaction, index) => (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.02 * index }}
                    className="bg-white/40 backdrop-blur-sm rounded-xl p-4 mb-3 border border-gray-200/60"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1.5">
                          <div
                            className={`px-2 py-0.2 rounded-full ${transaction.type === "depo_sol"
                              ? "bg-blue-100/70 text-blue-700"
                              : "bg-yellow-100/70 text-yellow-700"
                              }`}
                          >
                            <span className="text-[8px] font-bold text-center">
                              {transaction.type.toUpperCase()}
                            </span>
                          </div>
                          <span className="text-[8px] text-gray-500">
                            {transaction.date}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {transaction.description}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-lg font-bold text-gray-900">
                          {formatMoney(transaction.montant)}
                        </p>
                        <p className="text-xs font-medium text-gray-500">
                          {transaction.devise}
                        </p>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                      <p className="text-xs text-gray-400">
                        ID: {transaction.id.substring(0, 8)}...
                      </p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8 bg-gray-50/50 rounded-xl border border-gray-200/50">
                  <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {t('dashboard:filters.noResults')}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t('dashboard:filters.tryOtherFilters')}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Account Modal */}
      <AnimatePresence>
        {modalVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center z-50"
            onClick={() => setModalVisible(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-2xl md:rounded-2xl p-6 w-full md:max-w-md max-h-[70vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-200">
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {t('dashboard:selectAccount')}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('dashboard:accountsAvailable', { count: accounts.length })}
                  </p>
                </div>
                <button
                  onClick={() => setModalVisible(false)}
                  className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <div className="space-y-2">
                {accounts.map((account) => {
                  const isSelected = selectedAccount?.code === account.code;
                  const isSOL = account.type_compte === "SOL";

                  return (
                    <motion.button
                      key={account.code}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSelectedAccount(account);
                        setModalVisible(false);
                      }}
                      className={`w-full p-4 rounded-xl border transition-all ${isSelected
                        ? "bg-blue-50/30 border-blue-900"
                        : "bg-white border-gray-200 hover:border-blue-900/50"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${isSOL ? "bg-blue-100" : "bg-blue-900/10"
                            }`}
                        >
                          {isSOL ? (
                            <Users className="w-5 h-5 text-blue-600" />
                          ) : (
                            <DollarSign className="w-5 h-5 text-blue-900" />
                          )}
                        </div>

                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`text-sm font-bold ${isSelected ? "text-blue-900" : "text-gray-900"
                                }`}
                            >
                              #{account.code}
                            </span>
                            <div
                              className={`px-2 py-0.5 rounded text-xs font-bold ${isSOL
                                ? "bg-blue-100 text-blue-700"
                                : "bg-blue-900/10 text-blue-900"
                                }`}
                            >
                              {account.type_compte}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {account.devise || t('dashboard:unspecifiedCurrency')}
                          </p>
                        </div>

                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected
                            ? "bg-blue-900 border-blue-900"
                            : "border-gray-300"
                            }`}
                        >
                          {isSelected && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                          )}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Security Reminder Modal */}
      <AnimatePresence>
        {showSecurityReminder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 relative"
            >
              {/* Background Pattern */}
              <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 -z-10" />

              <div className="p-6 pt-8 text-center relative">
                <button
                  onClick={handleDismissReminder}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-white/50 p-1 rounded-full hover:bg-gray-100"
                >
                  <X size={20} />
                </button>

                <div className="mx-auto w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mb-6 shadow-sm ring-4 ring-orange-50">
                  <ShieldAlert size={32} />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('settings:security.reminderTitle')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-8">
                  {t('settings:security.reminderMessage')}
                </p>

                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/profile?tab=security')}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 px-6 rounded-xl text-base font-semibold shadow-lg shadow-orange-200 transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                  >
                    {t('settings:security.setupNow')}
                    <ArrowRight size={18} />
                  </button>

                  <button
                    onClick={handleDismissReminder}
                    className="text-gray-500 hover:text-gray-800 text-sm font-medium py-2 transition-colors"
                  >
                    {t('settings:security.later')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <KYCVerificationModal
        isOpen={kycModalOpen}
        onClose={() => setKycModalOpen(false)}
        verificationUrl={kycVerificationUrl}
      />

      <RestrictionModal
        isOpen={restrictionModalOpen}
        onClose={() => setRestrictionModalOpen(false)}
        currentLanguage={currentLanguage}
      />
    </div >
  );
};

export default Home;
